import { useEffect, useState } from 'react';
import type { PeriodKind, TaxStatus } from '@otter/shared';
import { api } from '../../lib/api';
import { fmtDate, todayIso } from '../../lib/format';
import { DateInput } from '../../components/DateInput';

type Props = {
  onToast: (msg: string, kind?: 'ok' | 'err') => void;
  onError: (e: unknown) => void;
};

type Row = {
  id: string;
  modelCode: string;
  description: string;
  periodKind: PeriodKind;
  fiscalYear: number;
  periodLabel: string;
  dueDate: string;
  status: TaxStatus;
  estimatedAmount: string | null;
  actualAmount: string | null;
  notes: string | null;
};

export function Fiscal({ onToast, onError }: Props) {
  const [items, setItems] = useState<Row[]>([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [showCreate, setShowCreate] = useState(false);

  async function load() {
    try {
      const r = await api<{ items: Row[] }>('/admin/tax-obligations', {
        query: { fiscalYear: year },
      });
      setItems(r.items);
    } catch (e) {
      onError(e);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  async function setStatus(id: string, status: TaxStatus) {
    try {
      await api(`/admin/tax-obligations/${id}`, { method: 'PATCH', body: { status } });
      onToast('Estado actualizado', 'ok');
      void load();
    } catch (e) {
      onError(e);
    }
  }

  async function remove(id: string) {
    if (!confirm('¿Borrar esta obligación fiscal?')) return;
    try {
      await api(`/admin/tax-obligations/${id}`, { method: 'DELETE' });
      onToast('Obligación borrada', 'ok');
      void load();
    } catch (e) {
      onError(e);
    }
  }

  return (
    <div className="adm-section">
      <div className="stitle">Fiscal · obligaciones tributarias</div>
      <h2 style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
        Modelos {year}
        <input
          type="number"
          value={year}
          onChange={(e) => setYear(Number.parseInt(e.target.value, 10) || year)}
          style={{
            width: 80,
            padding: '4px 8px',
            border: '1px solid var(--line)',
            background: 'var(--panel)',
            color: 'var(--ink)',
            borderRadius: 4,
            fontFamily: 'Geist Mono, monospace',
            fontSize: 12,
          }}
        />
        <button
          className="btn primary"
          style={{ marginLeft: 'auto' }}
          onClick={() => setShowCreate(true)}
        >
          <span className="plus">+</span> Nueva obligación
        </button>
      </h2>
      <div className="card">
        <table className="t">
          <thead>
            <tr>
              <th>Modelo</th>
              <th>Descripción</th>
              <th>Periodo</th>
              <th>Plazo</th>
              <th>Estado</th>
              <th className="num">Estimado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}>
                <td className="mono">
                  <b>{it.modelCode}</b>
                </td>
                <td>{it.description}</td>
                <td className="mono" style={{ color: 'var(--muted)' }}>
                  {it.periodLabel}
                </td>
                <td className="mono">{fmtDate(it.dueDate)}</td>
                <td>
                  <select
                    value={it.status}
                    onChange={(e) => setStatus(it.id, e.target.value as TaxStatus)}
                    style={{
                      padding: '2px 6px',
                      border: '1px solid var(--line)',
                      background: 'var(--panel)',
                      color: 'var(--ink)',
                      borderRadius: 4,
                      fontFamily: 'Geist Mono, monospace',
                      fontSize: 11,
                    }}
                  >
                    <option value="proximo">próximo</option>
                    <option value="presentado">presentado</option>
                    <option value="no_aplica">no aplica</option>
                  </select>
                </td>
                <td className="num">{it.estimatedAmount ?? '—'}</td>
                <td>
                  <button className="btn sm danger" onClick={() => remove(it.id)}>
                    borrar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && (
          <div className="empty">sin obligaciones registradas en {year}</div>
        )}
      </div>
      {showCreate && (
        <CreateTaxModal
          year={year}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void load();
            onToast('Obligación creada', 'ok');
          }}
          onError={onError}
        />
      )}
    </div>
  );
}

function CreateTaxModal(props: {
  year: number;
  onClose: () => void;
  onCreated: () => void;
  onError: (e: unknown) => void;
}) {
  const [modelCode, setModelCode] = useState('303');
  const [description, setDescription] = useState('IVA · trimestral');
  const [periodKind, setPeriodKind] = useState<PeriodKind>('quarterly');
  const [periodLabel, setPeriodLabel] = useState(`Q1 ${props.year}`);
  const [dueDate, setDueDate] = useState(todayIso());
  const [estimatedAmount, setEstimatedAmount] = useState(0);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api('/admin/tax-obligations', {
        method: 'POST',
        body: {
          modelCode,
          description,
          periodKind,
          fiscalYear: props.year,
          periodLabel,
          dueDate,
          estimatedAmount,
        },
      });
      props.onCreated();
    } catch (err) {
      props.onError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={props.onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Nueva obligación fiscal</h3>
        <div className="row2">
          <div className="field">
            <label>Modelo</label>
            <input value={modelCode} onChange={(e) => setModelCode(e.target.value)} required />
          </div>
          <div className="field">
            <label>Periodo</label>
            <select
              value={periodKind}
              onChange={(e) => setPeriodKind(e.target.value as PeriodKind)}
            >
              <option value="monthly">mensual</option>
              <option value="quarterly">trimestral</option>
              <option value="annual">anual</option>
            </select>
          </div>
        </div>
        <div className="field">
          <label>Descripción</label>
          <input value={description} onChange={(e) => setDescription(e.target.value)} required />
        </div>
        <div className="row2">
          <div className="field">
            <label>Etiqueta del periodo</label>
            <input
              value={periodLabel}
              onChange={(e) => setPeriodLabel(e.target.value)}
              placeholder="Q1 2026"
              required
            />
          </div>
          <div className="field">
            <label>Plazo</label>
            <DateInput value={dueDate} onChange={setDueDate} required />
          </div>
        </div>
        <div className="field">
          <label>Estimado (€)</label>
          <input
            type="number"
            step="0.01"
            value={estimatedAmount}
            onChange={(e) => setEstimatedAmount(Number.parseFloat(e.target.value) || 0)}
          />
        </div>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={props.onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            Crear
          </button>
        </div>
      </form>
    </div>
  );
}
